/**
 * Ingestion Pipeline for Captain Mesh Transport
 */
import { offlineDb } from "@/db/offlineDb";
import { saveAndApplyEvent } from "../db";
import { computeLocalFrontiers, computeMissingEventsForPeer } from "./cursors";
import { isStaleEvent } from "../domain/staleness";

/**
 * Handles all incoming messages from an authenticated WebRTC DataChannel.
 */
export async function handleDataChannelMessage(peerId, message, peerManager) {
  if (message.type === "SYNC_REQUEST") {
    // Peer is giving us their causal frontier and asking for missing events
    const missingEvents = await computeMissingEventsForPeer(message.frontiers);
    if (missingEvents.length > 0) {
      peerManager.sendToPeer(peerId, {
        type: "EVENT_BATCH",
        events: missingEvents
      });
    }
  } 
  
  else if (message.type === "EVENT_BATCH") {
    for (const event of message.events) {
      await processIncomingEvent(peerId, event, peerManager);
    }
  }

  else if (message.type === "EVENT_ACK") {
    // Optional: We can mark the event as sync_state="peer_acked" in our local DB
    // if we want to track delivery. For M1, we just acknowledge receipt silently.
    if (message.status === "PERSISTED") {
      // Event successfully delivered to peer
    }
  }

  else if (message.type === "HEARTBEAT") {
    peerManager.sendToPeer(peerId, { type: "HEARTBEAT_ACK" });
  }
}

/**
 * The Strict Ingestion Pipeline for a single event.
 * Never ACK before durable local persistence.
 */
export async function processIncomingEvent(peerId, event, peerManager) {
  // 1. Validate envelope/schema (basic check)
  if (!event || !event.event_id || !event.aggregate_id || !event.type) {
    console.warn("[MeshIngest] Invalid event envelope received", event);
    return;
  }

  // 1b. A day-old capture from a colleague's pad is not tonight's table. Stored,
  // it would be flushed here, refused as stale and painted CONFLICT on this
  // board too (2026-10-24, the first night pads linked). Acknowledged so the
  // sender stops offering it, never kept.
  if (isStaleEvent(event)) {
    peerManager.sendToPeer(peerId, { type: "EVENT_ACK", event_id: event.event_id, status: "PERSISTED" });
    return;
  }

  // 2. Verify causality metadata
  // If the event has a parent, do we have it? 
  // (In M0 the reducer handles waiting parents gracefully, but we could request it here).
  // For M1, the reducer will safely ignore unparented events until the parent arrives.
  // The DataChannel is ordered, so the parent should have arrived first if sent in the same batch.

  // 3. Duplicate Protection
  const existing = await offlineDb.captain_events.get(event.event_id);
  
  if (existing) {
    // Already have it -> ACK immediately
    peerManager.sendToPeer(peerId, {
      type: "EVENT_ACK",
      event_id: event.event_id,
      status: "PERSISTED",
    });
    return;
  }

  // 4. Persist durably & Feed M0 reducer & Persist projection
  // saveAndApplyEvent wraps all three in a single IndexedDB transaction.
  try {
    await saveAndApplyEvent(event);
  } catch (err) {
    console.error(`[MeshIngest] Failed to persist event ${event.event_id}`, err);
    return; // DO NOT ACK on crash/failure!
  }

  // 5. ACK only after durable persistence succeeds
  peerManager.sendToPeer(peerId, {
    type: "EVENT_ACK",
    event_id: event.event_id,
    status: "PERSISTED",
  });
}
